<style lang="less" scoped>
@import "/styles/SiteColors.less";
@import "/styles/floating-label.less";

.search-group {
    // Single source of truth for the clear button's footprint, so the input's reserved
    // padding-right below can never drift out of sync with the button's actual size.
    --clear-btn-size: 2rem;

    .clear {
        position: absolute;
        right: 0;
        z-index: 3;
        color: black;
        border: none;
        box-shadow: none;
        padding: calc((var(--clear-btn-size) - 1em) / 2);

        &:focus {
            outline-offset: 0;
            outline-color: @active-black;
        }
    }
}

.accessible-search-input {
    /**
    * Remove default clear icon of search input type.
    * https://stackoverflow.com/a/77190241
    */
    appearance: textfield;
    outline-offset: -2px; // Fix outline style in Safari
    padding-right: var(--clear-btn-size); // Reserve space so entered text doesn't overlap the clear button

    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
        -webkit-appearance: none;
    }
}

.search-group .clear {
    top: 50%;
    translate: 0 -50%;
    line-height: 1;
}

.input-group {
    position: relative;

    .floating-label {
        left: 8px;
    }
}
</style>

<template>
    <div class="search-group" role="search" :class="$attrs.class">
        <label v-if="labelOutsideInputGroup" :class="labelClass" :for="id">{{ label }}</label>
        <div class="input-group">
            <template v-if="floatingLabel">
                <div class="floating-label" :class="{ 'floating-label-active': isFloatingLabelActive }">
                    <label class="control-label" :for="id">{{ label }}</label>
                </div>
            </template>
            <label v-else-if="!labelOutsideInputGroup" :class="labelClass" :for="id">{{ label }}</label>
            <input :id="id" type="text" ref="inputRef" v-model="searchTerm" :placeholder="floatingLabel ? '' : placeholder" :autocomplete="autocomplete" :autofocus="autofocus" :aria-describedby="describedBy" :aria-invalid="describedBy ? 'true' : undefined" class="accessible-search-input rounded z-0" :class="[inputClass, { 'floating-label-input': floatingLabel }]" @focus="isFocused = true" @blur="isFocused = false" @submit.prevent />
            <button v-show="(searchTerm || '').length > 0" type="button" class="clear btn btn-bare icon icon-close rounded" aria-label="Clear search" @click.stop="clearInput" />
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, toRefs, useAttrs } from "vue";

export default defineComponent({
    inheritAttrs: false,
    emits: ["update:modelValue", "clear"],
    props: {
        modelValue: {
            type: [String, null],
            default: "",
        },
        placeholder: {
            type: String,
            default: "Filter...",
        },
        label: {
            type: String,
            default: "Search input",
        },
        labelClass: {
            type: String,
            default: "sr-only",
        },
        labelOutsideInputGroup: {
            type: Boolean,
            default: false,
        },
        inputClass: {
            type: String,
            default: "",
        },
        autocomplete: {
            type: String,
            default: undefined,
        },
        autofocus: {
            type: Boolean,
            default: false,
        },
        floatingLabel: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, { emit }) {
        const { modelValue, floatingLabel } = toRefs(props);
        const inputRef = ref<HTMLInputElement>(null!);
        const attrs = useAttrs();
        const isFocused = ref(false);

        const id = computed(() => attrs.id as string);
        // inheritAttrs is off, so anything the caller needs on the input has to be forwarded here.
        const describedBy = computed(() => attrs["aria-describedby"] as string | undefined);
        const searchTerm = computed({
            get() {
                return modelValue.value;
            },
            set(newValue) {
                emit("update:modelValue", newValue);
            },
        });

        const isFloatingLabelActive = computed(() => floatingLabel.value && (isFocused.value || !!(searchTerm.value || "").length));

        const clearInput = () => {
            searchTerm.value = "";
            emit("clear");

            if (inputRef.value) {
                inputRef.value.focus();
            }
        };

        return {
            id,
            describedBy,
            searchTerm,
            clearInput,
            inputRef,
            isFocused,
            isFloatingLabelActive,
        };
    },
});
</script>
